import { Navigate, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import BookSpinner from "@/components/common/BookSpinner";

/**
 * AdminRoute — guards routes that require admin or super_admin role.
 *
 * Behaviour:
 * 1. While the auth state is loading, shows a loading spinner.
 * 2. If the user is not authenticated, redirects to the login page.
 * 3. If the user is authenticated but does NOT have an admin or super_admin
 *    role, redirects to the home page.
 * 4. Otherwise, renders the child routes via <Outlet />.
 */
export default function AdminRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || "fr";

  // Still checking auth state — show a centered spinner
  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <BookSpinner size="lg" />
      </div>
    );
  }

  // Not authenticated at all — redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to={`/${currentLang}/login`} replace />;
  }

  // Authenticated but not an admin — redirect to home
  const isAdmin = user.role === "admin" || user.role === "super_admin";

  if (!isAdmin) {
    return <Navigate to={`/${currentLang}/`} replace />;
  }

  // All good — render admin child routes
  return <Outlet />;
}
